import { useRoute } from "@react-navigation/native";
import React, { FC, useCallback, useEffect } from "react";
import { FlatList, ScrollView, StyleSheet, Text, View } from "react-native";
import { useSelector } from "react-redux";
import { EmptyCart } from "../components";
import { kStyleConst } from "../kStyle";
import { useFetchDraftFromId } from "../redux/draftCart/thunk";
import BrandCart from "../components/BrandCart";
import OrderSummary from "../components/OrderSummary";
import { kColors } from "src/kColors";
import { useConnectionRequestModal, useOpenDraftOrderModal } from "src/hooks/modals";
import { useToast } from "src/hooks/toast";
import { useIsMobileAndTablet } from "src/hooks/ui";

const P102_Checkout: FC = () => {
  const isMobileTablet = useIsMobileAndTablet();
  const route = useRoute<{ name: string; key: string; params: { draftId?: string } }>();
  useFetchDraftFromId(route?.params?.draftId ?? "");

  const rCart = useSelector(({ UserReducer }) => UserReducer.cart);
  const toast = useToast();
  const connectionRequestModal = useConnectionRequestModal();
  const openDraftOrderModal = useOpenDraftOrderModal();

  const _cartKeyExtractor = useCallback((myCartItem) => myCartItem.name, []);

  const _handleOpenRequestBrandModal = async (brandId: string, brandName: string) => {
    const response = await connectionRequestModal.openAsync({ brandId, brandName });
    if (response.status === "closed") return;
    toast({
      description:
        "Your connection request was sent. Your cart will be saved here and once brand approved your request, you can come back and checkout.",
    });
  };

  useEffect(() => {
    if (route?.params?.draftId) {
      openDraftOrderModal.open({ draftId: route.params.draftId });
    }
  }, [openDraftOrderModal, route?.params?.draftId]);

  return (
    <ScrollView
      maintainVisibleContentPosition={{ minIndexForVisible: 1 }}
      style={styles.checkoutScrollView}
      contentContainerStyle={[
        styles.checkOutContainer,
        isMobileTablet ? [styles.my2, styles.mx4] : [styles.flexRow, styles.my10, styles.mx14],
      ]}
    >
      {/* SHOPPING CART SECTION */}
      <View style={styles.checkoutProductContainer}>
        <Text style={styles.label}>Shopping Bag</Text>
        <FlatList
          data={rCart}
          keyExtractor={_cartKeyExtractor}
          renderItem={({ item }) => <BrandCart item={item} onRequestToConnect={_handleOpenRequestBrandModal} />}
          scrollEnabled={true}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={EmptyCart}
        />
      </View>
      {/* END SHOPPING CART SECTION */}
      {/* ORDER SUMMARY SECTION */}
      <View style={isMobileTablet ? styles.mt3 : styles.ml10}>
        <Text style={styles.label}>Order Summary</Text>
        <View style={styles.divider} />
        <OrderSummary />
      </View>
      {/* END ORDER SUMMARY SECTION */}
    </ScrollView>
  );
};

export const P102 = React.memo(P102_Checkout);

const styles = StyleSheet.create({
  checkOutContainer: {
    paddingBottom: 10 * kStyleConst.kSpacing,
  },
  checkoutProductContainer: {
    alignItems: "stretch",
    flex: 1,
    flexDirection: "column",
    minWidth: 300,
  },
  checkoutScrollView: {
    alignContent: "center",
  },
  divider: {
    backgroundColor: kColors.gray300,
    height: 1,
    width: "100%",
  },
  flexRow: {
    flexDirection: "row",
  },
  label: {
    color: kColors.brand300,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 1.1,
    lineHeight: 30,
    textTransform: "uppercase",
  },
  ml10: {
    marginLeft: 10 * kStyleConst.kSpacing,
  },
  mt3: {
    marginTop: 3 * kStyleConst.kSpacing,
  },
  mx14: {
    marginHorizontal: 14 * kStyleConst.kSpacing,
  },
  mx4: {
    marginHorizontal: 4 * kStyleConst.kSpacing,
  },
  my10: {
    marginVertical: 10 * kStyleConst.kSpacing,
  },
  my2: {
    marginVertical: 2 * kStyleConst.kSpacing,
  },
});
